import { BasicColumnTypes } from '@opengov/ogql-types';
import { lensColumnToStorageReportColumn } from '@opengov/reporting-transforms/columns';
import {
  EGroupType,
  IAgGridColumnConfig,
  IColumnDataSource,
  IColumnSource,
  IConditionalReportColumn,
  IDatasetLensColumn,
  IExpandedGroupKeys,
  IGroupExpression,
  IReportCoaDimensionMetadata,
  IReportConfiguration,
  IReportConfigurationDetailTableQuery,
  IReportConfigurationDimensionalSummaryQuery,
  IReportConfigurationPresentationSummary,
  IReportConfigurationVisualizationQuery,
  IReportDimension,
  IReportTarget,
  IRowGroup,
  OrganizedByColumnType,
  ReportControlType,
  ReportTableTabs,
  SortDirection,
  createColumnAlias,
  createControlBlockAlias,
  createControlBlockIndex,
  createHiddenControlColumns,
  createReportConfiguration,
  createReportConfigurationLayers,
  createSingleColor,
  reportConfigurationsMatch,
  ColumnOperandIdentifier,
  IAccountSummaryRounding,
  isColumnDataSource,
  isMultiHierarchyColumnDataSource,
  MultiHierarchyColumnDataSource,
  IStringConstantExpression,
  IAccountSummary,
  HiddenColumnGroupIndicator,
  isMultiHierarchyAccountSummary,
  MhDetailsViewConfig,
  StackingOptions,
  CalendarUnit,
  calendarUnitToDateColumnName,
  DateColumnName,
  COLUMN_GROUP_ORGANIZE_BY_INDEX
} from '@opengov/reporting-types';
import {
  EColumnDimensionType,
  EOgqlAggregateColumnOperators,
  EReportColumnTypes,
  IDimensionalDateReportColumn,
  IDimensionalReportColumn,
  IInfixReportColumn,
  IPercentOfTotalReportColumn,
  IReportColumn,
  IStorageReportColumn,
  categorizedByNoneColumnObject,
  createAggregateReportColumn,
  createDimensionalDateReportColumn,
  createDimensionalReportColumn,
  createPercentOfTotalReportColumn
} from '@opengov/reporting-types/src/reportColumns-immer';
import {
  IColumnFilterBooleanOperator,
  IColumnFilterComparison,
  ISegmentFilter,
  createColumnFilter,
  createSegmentFilter
} from '@opengov/reporting-types/src/reportFilters';
import isBlank from '@sedan-utils/is-blank';
import { castDraft, produce } from 'immer';
import { isEqual, mapValues } from 'lodash';

import { composeStateMachine } from '../../../module';
import { IRowsColumnPayload, IShowCoaAccountWithColumnsColumn } from '../../dimensionalTable/types';
import * as currentReportActions from './currentReportActions';
import { IReportConfigurationState } from './records';
import { ISetAccountSummarySelectedColumnPayload, ReportConfigurationStatus } from './types';
import { withImmerReducer, withNewReportConfig, withVisualizationQuery } from './utils';
import * as visualizationQueryActions from './visualizationQueryActions';

export const ReportConfigurationInitialState: IReportConfigurationState = {
  reportConfigurationStatus: ReportConfigurationStatus.Loading,
  currentReportConfiguration: null,
  shouldPersistReportConfigurations: true
};

type IReportConfigurationMutator<PAYLOAD> = (oldState: IReportConfiguration, payload: PAYLOAD) => IReportConfiguration;

type IReportConfigurationMutatorWithoutPayload = (oldState: IReportConfiguration) => IReportConfiguration;

// applyFilter(...) adds or replaces an existing filter with the given column name and operator
const applyFilter = (
  filters: IColumnFilterComparison[],
  state:
    | IReportConfigurationDetailTableQuery
    | IReportConfigurationVisualizationQuery
    | IReportConfigurationDimensionalSummaryQuery
) => {
  if (filters.length === 0) {
    return state;
  }

  const newFilters = filters.map(createColumnFilter);

  if (!state) return;

  state.filter = (state.filter || [])
    .filter((f) => newFilters.every((newFilter) => f.columnName !== newFilter.columnName))
    .concat(newFilters);

  return state;
};

// applyFilterBooleanOperators(...) adds or replaces an existing filter boolean operator
export const applyFilterBooleanOperators = (
  filterBooleanOperators: IColumnFilterBooleanOperator[],
  state: IReportConfigurationDetailTableQuery | IReportConfigurationVisualizationQuery
): IReportConfigurationDetailTableQuery | IReportConfigurationVisualizationQuery => {
  if (filterBooleanOperators.length === 0 || !state.filterBooleanOperators) {
    return state;
  }

  state.filterBooleanOperators = state.filterBooleanOperators
    .filter((b) =>
      filterBooleanOperators.every((newFilterBooleanOperator) => b.columnName !== newFilterBooleanOperator.columnName)
    )
    .concat(filterBooleanOperators);

  return state;
};

const clearDateFilters = (state: IReportConfiguration): void => {
  state.visualizationQuery.filter = [];
  state.detailTableQuery.filter = [];
  state.dimensionalSummaryQueries = state.dimensionalSummaryQueries.map((query) => {
    query.filter = [];
    return query;
  });
};

// creates a new instance of report configuration without an id
export const mutateCurrentReportConfigurationInState =
  <PAYLOAD>(update: IReportConfigurationMutator<PAYLOAD>) =>
  (state: IReportConfigurationState, payload: PAYLOAD): IReportConfigurationState =>
    produce(state, (newState) => {
      // if we are mutating remove the id because that's the old one fool!
      newState.currentReportConfiguration.id = null;
      const newOne = newState.currentReportConfiguration;
      newState.currentReportConfiguration = castDraft(createReportConfiguration(update(newOne, payload)));
    });

// creates a new instance of report configuration without an id
export const mutateReportConfigurationStateWithoutPayload =
  (update: IReportConfigurationMutatorWithoutPayload) =>
  (state: typeof ReportConfigurationInitialState): IReportConfigurationState =>
    produce(state, (newState) => {
      // if we are mutating remove the id which is creating a new one
      newState.currentReportConfiguration.id = null;
      update(newState.currentReportConfiguration);
    });

// comparing the salient bits just for query changes
export const reportConfigurationsMatchForQueries = (
  current: IReportConfiguration,
  r: IReportConfiguration
): boolean => {
  const newCurrent = produce(current, (draft) => {
    // ignore changes to showTotals, we don't need to reissue non-totals queries when that changes.
    draft.detailTableQuery.showTotals = r.detailTableQuery.showTotals;
  });

  return (
    r.viewId === current.viewId &&
    r.reportId === current.reportId &&
    isEqual(r.layers.target, current.layers.target) &&
    isEqual(r.detailTableQuery, newCurrent.detailTableQuery) &&
    isEqual(r.visualizationQuery, current.visualizationQuery)
  );
};

export const reportConfigurationsMatchForDimensionalQueries = (
  current: IReportConfiguration,
  r: IReportConfiguration
): boolean => {
  return (
    r.viewId === current.viewId &&
    r.reportId === current.reportId &&
    isEqual(r.dimensionalSummaryQueries, current.dimensionalSummaryQueries) &&
    isEqual(
      r.presentationConfiguration.dimensionalSummaryBlockNames,
      current.presentationConfiguration.dimensionalSummaryBlockNames
    )
  );
};

export const reportConfigurationsMatchForAccountSummary = (
  current: IReportConfiguration,
  next: IReportConfiguration
): boolean => {
  // Excluding expandedGroupKeys from equality comparison
  return (
    current.viewId === next.viewId &&
    current.reportId === next.reportId &&
    isEqual(current.accountSummary.asOfDate, next.accountSummary.asOfDate) &&
    isEqual(current.accountSummary.columnGroups, next.accountSummary.columnGroups) &&
    isEqual(current.accountSummary.filters, next.accountSummary.filters) &&
    isEqual(current.accountSummary.formatting, next.accountSummary.formatting)
  );
};

export const columnToSortingPossibilities = (column) => {
  const segmentCodeColumnRef = column.accountCodeColumnRef;
  const hasCodeColumn = !!segmentCodeColumnRef;
  const descriptionCodeColumnRef = hasCodeColumn ? column.descriptionColumnRef : column.name;
  // This is a convention. The meta data does not explicitly tie the order column to the description
  const segmentCodeOrder = hasCodeColumn ? `${segmentCodeColumnRef}_order` : `${column.name}_order`;

  return {
    hasCodeColumn,
    segmentCodeColumnRef,
    descriptionCodeColumnRef,
    segmentCodeOrder
  };
};

export const deriveNextSort = (
  oldColumn: IDimensionalReportColumn,
  newColumn: IDimensionalReportColumn,
  oldSortColumn: string,
  oldSortDirection: SortDirection
) => {
  const oldPossibilities = columnToSortingPossibilities(oldColumn);
  const newPossibilities = columnToSortingPossibilities(newColumn);

  let sortColumnName = oldSortColumn || '';
  let sortDirection = oldSortDirection;

  if (sortColumnName === oldPossibilities.segmentCodeOrder) {
    // it was a coa code sort
    sortColumnName = newPossibilities.segmentCodeOrder;
  } else if (sortColumnName === oldPossibilities.segmentCodeColumnRef) {
    // it was a code order
    // but maybe this new one doesn't have a code
    if (newPossibilities.hasCodeColumn) {
      sortColumnName = newPossibilities.segmentCodeColumnRef;
    } else {
      sortColumnName = newPossibilities.segmentCodeOrder;
      sortDirection = SortDirection.ASC;
    }
  } else if (!isBlank(sortColumnName)) {
    // is was a description order
    sortColumnName = newPossibilities.descriptionCodeColumnRef;
  }

  return {
    sortColumnName,
    sortDirection
  };
};

export const getCoaColumnFromSelection = ({
  coaDimensionMetadata,
  selection
}: {
  coaDimensionMetadata: IReportCoaDimensionMetadata;
  selection: [string, string];
}): IDimensionalReportColumn => {
  const { ledgerTypes, segments } = coaDimensionMetadata;
  const dimensionId = coaDimensionMetadata.dimensionId;
  const name = selection[1];
  let displayName = '';

  const matchingLedgerType = ledgerTypes?.find((ledgerType) => ledgerType.label === selection[0]);
  const matchingSegment = segments?.find((segment) => segment.label === selection[0]);

  if (matchingLedgerType && matchingLedgerType.categoryLevels && matchingLedgerType.categoryLevels.length > 0) {
    const matchingCategoryLevel = matchingLedgerType.categoryLevels.find((level) => level.columnRef === name);

    if (matchingCategoryLevel && matchingCategoryLevel.label) {
      displayName = matchingCategoryLevel.label;
    }
  } else if (matchingSegment && matchingSegment.categoryLevels && matchingSegment.categoryLevels.length > 0) {
    const matchingCategoryLevel = matchingSegment.categoryLevels.find((level) => level.columnRef === name);

    if (matchingCategoryLevel && matchingCategoryLevel.label) {
      displayName = matchingCategoryLevel.label;
    }
  }

  return createDimensionalReportColumn({
    dimensionId,
    dimensionType: EColumnDimensionType.CHART_OF_ACCOUNTS,
    displayName,
    joinColumnName: coaDimensionMetadata.joinColumnName,
    name
  });
};

export const getDateColumnRecordFromSelection = ({
  dateDimension,
  lensDateColumns,
  lensYearColumns,
  selection
}: {
  dateDimension?: IReportDimension;
  lensDateColumns?: IDatasetLensColumn[];
  lensYearColumns?: IDatasetLensColumn[];
  selection: [string, CalendarUnit];
}): IDimensionalDateReportColumn | IStorageReportColumn => {
  const name = selection[0];
  const calendarUnit = selection[1];
  let dimensionId;
  let displayName;
  let dateColumnName;

  if (dateDimension && dateDimension.columns) {
    const dateColumnNameFromCalendarUnit = calendarUnitToDateColumnName[calendarUnit];
    const dateColumn = dateDimension.columns.find((c) => c.name === dateColumnNameFromCalendarUnit);

    dateColumnName = dateColumn && dateColumn.name;
  }

  const selectedYearColumn = lensYearColumns.filter((column) => column.name && column.name === name)[0];

  // In this case the selected column is a numeric fiscal year (YYYY)
  if (selectedYearColumn) {
    return lensColumnToStorageReportColumn(selectedYearColumn);
  }

  const selectedDateColumn = lensDateColumns.filter((column) => column.name && column.name === name)[0];

  if (selectedDateColumn && selectedDateColumn.joinedDimensionId) {
    dimensionId = selectedDateColumn.joinedDimensionId;
  } else {
    dimensionId = dateDimension.id;
  }

  if (selectedDateColumn && selectedDateColumn.label) {
    displayName = selectedDateColumn.label;
  }

  return createDimensionalDateReportColumn({
    dateColumnName,
    dimensionId,
    displayName,
    name
  });
};

export const getSmallestCategoryLevelIndex = (categoryLevelArray) => {
  categoryLevelArray.sort((a, b) => (a.index > b.index ? 1 : -1));
  return categoryLevelArray[0].index;
};

const setCoaAccountType = (
  oldState: IReportConfiguration,
  payload: IShowCoaAccountWithColumnsColumn | IReportCoaDimensionMetadata
) =>
  produce(oldState, (newState: IReportConfiguration) => {
    const { coaDimensionMetadata, selectedColumn, selectedRow, coaAccountType, selectedShow, queryIndex } =
      payload as any;
    const { dimensionId, ledgerTypes, joinColumnName } = coaDimensionMetadata;
    const ledgerTypeToSet = ledgerTypes.find((ledgerType) => ledgerType.filterValue === coaAccountType);
    const currentlySelectedLedgerType =
      ledgerTypes && ledgerTypes.find((ledgerType) => ledgerType.filterValue === selectedShow);
    const shouldUpdateRows = selectedRow && selectedRow[0] === currentlySelectedLedgerType.label;
    const shouldUpdateColumns = selectedColumn && selectedColumn[0] === currentlySelectedLedgerType.label;

    // might not have any categoryLevels
    const defaultColumnName = ledgerTypeToSet?.categoryLevels?.[0]?.columnRef;
    const defaultColumnDisplayName = ledgerTypeToSet?.categoryLevels?.[0]?.label;

    let accountCodeColumnRef;
    let descriptionColumnRef;

    if (ledgerTypeToSet?.categoryLevels?.[0]?.index === getSmallestCategoryLevelIndex(ledgerTypeToSet.categoryLevels)) {
      accountCodeColumnRef = ledgerTypeToSet.accountCodeColumnRef;
      descriptionColumnRef = ledgerTypeToSet.descriptionColumnRef;
    }

    const defaultCoaColumn: IDimensionalReportColumn = createDimensionalReportColumn({
      name: defaultColumnName,
      displayName: defaultColumnDisplayName,
      dimensionType: EColumnDimensionType.CHART_OF_ACCOUNTS,
      dimensionId,
      joinColumnName,
      accountCodeColumnRef,
      descriptionColumnRef
    });

    newState.dimensionalSummaryQueries[queryIndex].dimension.showAccountType = coaAccountType;

    if (shouldUpdateRows) {
      const newRowsColumn = deriveRowsColumn([ledgerTypeToSet.label, defaultColumnName], coaDimensionMetadata);
      const { sortColumnName, sortDirection } = deriveNextSort(
        oldState.dimensionalSummaryQueries[0].dimension.rowsColumn,
        newRowsColumn,
        oldState.dimensionalSummaryQueries[0].sortColumnName,
        oldState.dimensionalSummaryQueries[0].sortDirection
      );

      newState.dimensionalSummaryQueries[queryIndex].dimension.rowsColumn = newRowsColumn;
      newState.dimensionalSummaryQueries[queryIndex].sortColumnName = sortColumnName;
      newState.dimensionalSummaryQueries[queryIndex].sortDirection = sortDirection;
    }

    if (shouldUpdateColumns) {
      newState.dimensionalSummaryQueries[queryIndex].dimension.columnsColumn = defaultCoaColumn;
    }

    //Reset account segmentFilter
    if (ledgerTypeToSet) {
      const accountSegmentFilter = createSegmentFilter({
        label: ledgerTypeToSet.label,
        segmentId: ledgerTypeToSet.accountCodeColumnRef,
        value: ''
      });

      newState.detailTableQuery.segmentFilters = newState.detailTableQuery.segmentFilters.map((segment) => {
        if (segment.segmentId === accountSegmentFilter.segmentId) {
          return accountSegmentFilter;
        }
        return segment;
      });

      newState.dimensionalSummaryQueries[queryIndex].segmentFilters = newState.dimensionalSummaryQueries[
        queryIndex
      ].segmentFilters.map((segment) => {
        if (segment.segmentId === accountSegmentFilter.segmentId) {
          return accountSegmentFilter;
        }
        return segment;
      });
    }
  });

const setColumnsColumn = (oldState: IReportConfiguration, payload) =>
  produce(oldState, (newState) => {
    const { coaDimensionMetadata, dateDimension, lensDateColumns, lensYearColumns, selectedColumn } = payload;
    const lastColumnsColumn = oldState.dimensionalSummaryQueries[0].dimension.columnsColumn;
    const usingSameColumnsColumn = lastColumnsColumn.name === selectedColumn[0];
    // String enums aren't reverse mapped until TypeScript v3.7.3 so we need to
    // cast to any here to resolve ts checker, even though this works at compile time
    // In 3.7.3 we can do this: payload[1] in CalendarUnit
    const isDateColumn = Object.values(CalendarUnit).includes(selectedColumn[1]);
    const column = isDateColumn
      ? getDateColumnRecordFromSelection({
          lensDateColumns,
          lensYearColumns,
          dateDimension,
          selection: selectedColumn
        })
      : getCoaColumnFromSelection({
          coaDimensionMetadata,
          selection: selectedColumn
        });

    newState.dimensionalSummaryQueries = newState.dimensionalSummaryQueries.map((query) => {
      (query.dimension.columnsColumn as any) = column;
      return query;
    });
    if (!(isDateColumn && usingSameColumnsColumn)) {
      clearDateFilters(newState);
    }
  });

export const deriveRowsColumn = (selectedRow: [string, string], coaDimensionMetadata: IReportCoaDimensionMetadata) => {
  const selectedParentSegmentLabel = selectedRow[0];
  const selectedRowColumn = selectedRow[1];

  const rowsLedger = coaDimensionMetadata.ledgerTypes?.find((ledger) => ledger.label === selectedParentSegmentLabel);

  const column = getCoaColumnFromSelection({
    coaDimensionMetadata,
    selection: selectedRow
  });

  if (rowsLedger && selectedRowColumn === rowsLedger.accountCodeColumnRef) {
    // selectedRowColumn is object code
    column.descriptionColumnRef = rowsLedger.descriptionColumnRef;
    column.accountCodeColumnRef = rowsLedger.accountCodeColumnRef;
  } else {
    // selectedRow isn't an account_type
    const rowsSegment = coaDimensionMetadata.segments?.find(
      (segment) => segment.descriptionColumnRef === selectedRowColumn
    );

    const segmentIsCodeSegment = (coaDimensionMetadata.segments || []).reduce((acc, segment) => {
      if (segment.descriptionColumnRef === selectedRowColumn) {
        return true;
      }

      return acc;
    }, false);

    if (rowsSegment && segmentIsCodeSegment) {
      column.descriptionColumnRef = rowsSegment.descriptionColumnRef;
      column.accountCodeColumnRef = rowsSegment.accountCodeColumnRef;
    }
  }

  return column;
};

export const updateExpandedRowGroups = (
  state: IReportConfigurationState,
  payload: IExpandedGroupKeys = {},
  keepConfigurationId = false
): IReportConfigurationState => {
  return produce(state, (draft) => {
    const rc = draft.currentReportConfiguration;
    if (!isEqual(rc.accountSummary.expandedGroupKeys, payload)) {
      if (!keepConfigurationId) {
        rc.id = null;
      }
      rc.accountSummary.expandedGroupKeys = payload;
    }
  });
};

// the return type on this function makes this a "type predicate"
// that helps typescript understand unions and sub-types
export const isCalculation = (columnSource: IColumnSource): columnSource is IGroupExpression<ColumnOperandIdentifier> =>
  columnSource.type === EGroupType.Expression;

export const setRowGroup = (
  oldState: IReportConfiguration,
  payload: { rowGroup: IRowGroup; index: number }
): IReportConfiguration =>
  produce(oldState, (newState: IReportConfiguration) => {
    newState.accountSummary.rowGroups[payload.index] = payload.rowGroup;
  });

export const setAccountSummarySelectedColumn = (
  state: IReportConfigurationState,
  payload: ISetAccountSummarySelectedColumnPayload
): IReportConfigurationState => {
  const { coaDimensionMetadata, dateDimension, openColumnIndex, selectedColumn } = payload;

  // This is here to prevent state updates if payload is not correct.
  // selectedColumn MUST be an array of length:
  //  1: ILensColumnReference
  //  2: IDimensionColumnReference
  if (![1, 2].includes(selectedColumn.length)) {
    console.error(`Unexpected payload ${selectedColumn}`);
    return state;
  }

  return produce(state, (draft) => {
    const rc = draft.currentReportConfiguration;
    rc.id = null;
    const columnSource = rc.accountSummary.columnGroups[openColumnIndex] as IColumnDataSource;
    const isDateColumn =
      selectedColumn.length === 2 &&
      Object.values(calendarUnitToDateColumnName).includes(selectedColumn[1] as DateColumnName);
    const projection =
      selectedColumn.length === 1
        ? { columnName: selectedColumn[0] }
        : {
            dimension: { id: isDateColumn ? dateDimension.id : coaDimensionMetadata.dimensionId },
            columnName: selectedColumn[1],
            joinColumnName: selectedColumn[0]
          };
    // Splices rather than assigning, so picking a single column TRUNCATES to one organize-by level.
    // Assignment alone would leave a deeper level behind, still breaking the columns down by a field
    // this control never showed. This path is deliberately single-level; the column editor is where
    // a column group is deepened.
    columnSource.grouping.splice(COLUMN_GROUP_ORGANIZE_BY_INDEX, columnSource.grouping.length, projection);
  });
};

export const toggleHiddenLegendItems = (currentReportConfiguration: IReportConfiguration, category: string) => {
  return produce(currentReportConfiguration, (newReportConfiguration) => {
    const hiddenLegendItems = currentReportConfiguration.presentationConfiguration.hiddenLegendItems || [];
    const hiddenLegendItemsSet = new Set(hiddenLegendItems);
    if (hiddenLegendItemsSet.has(category)) {
      hiddenLegendItemsSet.delete(category);
    } else {
      hiddenLegendItemsSet.add(category);
    }
    newReportConfiguration.presentationConfiguration.hiddenLegendItems = [...hiddenLegendItemsSet];
  });
};

//When apply to hide column group headers we change the header label to the indicator label
export const applyHideColumnGroupHeaders = (
  currentReportConfiguration: IReportConfiguration,
  { hideColumnGroupHeaders, lensNameMap }: { hideColumnGroupHeaders: boolean; lensNameMap: Map<string, string> }
) => {
  return produce(currentReportConfiguration, (newReportConfiguration) => {
    const sourceColumnGroups = (newReportConfiguration.accountSummary as IAccountSummary).columnGroups.filter(
      (col): col is IColumnDataSource | MultiHierarchyColumnDataSource =>
        isColumnDataSource(col) || isMultiHierarchyColumnDataSource(col)
    );
    if (!newReportConfiguration.accountSummary) {
      throw new Error('Applying rounding control to a non-account-summary report config');
    }
    // hideColumnGroupHeaders is optional, so an untouched report reads back as undefined.
    // It has to be normalized before comparing, otherwise `undefined !== false` makes an
    // unchanged toggle look like a change and the loop below overwrites custom headers.
    const currentHideColumnGroupHeadersState = newReportConfiguration.accountSummary.hideColumnGroupHeaders ?? false;
    newReportConfiguration.accountSummary.hideColumnGroupHeaders = hideColumnGroupHeaders;
    if (currentHideColumnGroupHeadersState === hideColumnGroupHeaders) return;
    //The part that changes the column group headers when hidden is enabled
    for (const group of sourceColumnGroups) {
      (group.grouping[0] as IStringConstantExpression).constant =
        (hideColumnGroupHeaders ? HiddenColumnGroupIndicator : lensNameMap.get(group.id)) || HiddenColumnGroupIndicator;
    }
  });
};

export const reportConfigurationState = composeStateMachine(
  'report-configuration',
  ReportConfigurationInitialState,
  (compose) => ({
    // this should only be called in a route during initial app loading
    // or when popping history
    setCurrentReportConfiguration: compose.action((state, payload: IReportConfiguration) =>
      produce(state, (newState) => {
        newState.currentReportConfiguration = castDraft(payload);
      })
    ),

    setCurrentReportConfigurationAndForceRefresh: compose.action((state, payload: IReportConfiguration) =>
      produce(state, (newState) => {
        newState.currentReportConfiguration = castDraft(payload);
      })
    ),

    // Call this to force the middleware to refetch our queries
    refreshReportConfiguration: compose.payloadlessAction((state) => state),

    setReportConfigurationStatus: compose.action((state, status: ReportConfigurationStatus) =>
      produce(state, (newState) => {
        newState.reportConfigurationStatus = status;
      })
    ),
    updateReportConfiguration: compose.action((state, payload: IReportConfiguration) =>
      produce(state, (newState) => {
        const curr = state.currentReportConfiguration;
        if (curr && payload.id && reportConfigurationsMatch(curr, payload)) {
          newState.currentReportConfiguration = castDraft(payload);
        }
      })
    ),
    setColumnSort: compose.action(
      mutateCurrentReportConfigurationInState(
        (oldState, payload: { sortDirection: SortDirection; sortColumn: string }) =>
          produce(oldState, (newState) => {
            newState.detailTableQuery.sortDirection = payload.sortDirection;
            newState.detailTableQuery.sortColumn = payload.sortColumn;
            newState.detailTableQuery.paginationLimit = 250;
            newState.detailTableQuery.paginationOffset = 0;
          })
      )
    ),
    setTablePagination: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload: { limit: number; offset: number }) =>
        produce(oldState, (newState) => {
          newState.detailTableQuery.paginationLimit = payload.limit;
          newState.detailTableQuery.paginationOffset = payload.offset;
        })
      )
    ),
    setColumnFilters: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload: IColumnFilterComparison[]) =>
        produce(oldState, (newState) => {
          newState.visualizationQuery = applyFilter(
            payload,
            newState.visualizationQuery
          ) as IReportConfigurationVisualizationQuery;
          newState.detailTableQuery = applyFilter(
            payload,
            newState.detailTableQuery
          ) as IReportConfigurationDetailTableQuery;
          newState.detailTableQuery.paginationLimit = 250;
          newState.detailTableQuery.paginationOffset = 0;
          newState.dimensionalSummaryQueries = newState.dimensionalSummaryQueries.map(
            (q) => applyFilter(payload, q) as IReportConfigurationDimensionalSummaryQuery
          );
        })
      )
    ),
    setColumnFilterBooleanOperators: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload: IColumnFilterBooleanOperator[]) =>
        produce(oldState, (newState) => {
          newState.visualizationQuery = applyFilterBooleanOperators(
            payload,
            newState.visualizationQuery
          ) as IReportConfigurationVisualizationQuery;
          newState.detailTableQuery = applyFilterBooleanOperators(
            payload,
            newState.detailTableQuery
          ) as IReportConfigurationDetailTableQuery;
          newState.detailTableQuery.paginationLimit = 250;
          newState.detailTableQuery.paginationOffset = 0;
        })
      )
    ),
    setVisualizationQueryOrganizedByColumn: compose.action(
      mutateCurrentReportConfigurationInState(
        (
          oldState,
          payload: {
            selection: CalendarUnit | string;
            newOrgByColumn: IReportColumn | IDimensionalReportColumn;
            columnType: OrganizedByColumnType;
          }
        ) =>
          produce(oldState, (newState) => {
            newState.visualizationQuery.organizedBy.selection = payload.selection;
            newState.visualizationQuery.organizedBy.column = payload.newOrgByColumn;
            newState.visualizationQuery.organizedBy.columnType = payload.columnType;
          })
      )
    ),
    addBasicCalculatedColumnToDetailTable: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload: { newColumn: IInfixReportColumn }) =>
        produce(oldState, (newState) => {
          const columnName = `__${payload.newColumn.displayName
            .toLowerCase()
            .trim()
            .replace(/ /g, '_')
            .replace(/\W/g, '')}_${newState.detailTableQuery.columns.length}`;

          const column = JSON.parse(JSON.stringify(payload.newColumn));
          column.name = columnName;

          newState.detailTableQuery.columns.push(column as IReportColumn);
        })
      )
    ),
    addConditionalColumnToDetailTable: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload: { newColumn: IConditionalReportColumn<any> }) =>
        produce(oldState, (newState) => {
          const columnName = `__${payload.newColumn.displayName
            .toLowerCase()
            .trim()
            .replace(/ /g, '_')
            .replace(/\W/g, '')}_${newState.detailTableQuery.columns.length}`;

          const column = JSON.parse(JSON.stringify(payload.newColumn));
          column.name = columnName;

          newState.detailTableQuery.columns.push(column as IReportColumn);
        })
      )
    ),
    removeColumnFromDetailTable: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload: { columnNameToDelete: string }) =>
        produce(oldState, (newState) => {
          newState.detailTableQuery.columns = newState.detailTableQuery.columns.filter(
            (x) => x.name !== payload.columnNameToDelete
          );
        })
      )
    ),
    updateSegmentFilters: compose.action(
      mutateCurrentReportConfigurationInState((oldState, filters: ISegmentFilter[]) =>
        produce(oldState, (newState) => {
          newState.detailTableQuery.segmentFilters = filters;
          newState.dimensionalSummaryQueries = newState.dimensionalSummaryQueries.map((query) => {
            query.globalSegmentFilters = filters;
            return query;
          });
        })
      )
    ),
    updateDetailTableQueryGroupByColumns: compose.action(
      mutateCurrentReportConfigurationInState((oldState, columnNames: string[]) =>
        produce(oldState, (newState) => {
          newState.detailTableQuery.groupByColumns = columnNames;
          newState.detailTableQuery.paginationOffset = 0;
        })
      )
    ),
    updateSegmentFiltersForQueryIndex: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload) =>
        produce(oldState, (newState) => {
          const { segmentFilters, queryIndex } = payload;
          newState.dimensionalSummaryQueries[queryIndex].segmentFilters = segmentFilters;
        })
      )
    ),
    updateGroupByExpandedNodes: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload) =>
        produce(oldState, (newState) => {
          const { expandedNodes } = payload;
          newState.presentationConfiguration.groupByExpandedNodes = expandedNodes;
        })
      )
    ),
    setVisualizationQueryShowColumnName: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload: { columnName: string; columns }) =>
        produce(oldState, (newState) => {
          const { columnName, columns } = payload;
          const isShownByPercent = oldState.visualizationQuery.showColumnAsPercentOfTotal;
          const columnRenames = oldState.presentationConfiguration.columnNames;
          let newShowColumn;

          if (columnName === EOgqlAggregateColumnOperators.count) {
            newShowColumn = isShownByPercent
              ? createPercentOfTotalReportColumn({
                  name: EOgqlAggregateColumnOperators.count,
                  displayName: 'Count',
                  originalColumnType: EReportColumnTypes.aggregate,
                  type: BasicColumnTypes.Percent
                })
              : createAggregateReportColumn();
          } else if (isShownByPercent) {
            const columnToUse = columns.find((column) => column.name === columnName);
            newShowColumn = createPercentOfTotalReportColumn({
              ...columnToUse,
              originalColumnType: columnToUse.columnType,
              columnType: EReportColumnTypes.percentOfTotal,
              type: BasicColumnTypes.Percent
            });
          } else {
            newShowColumn = columns.find((column) => column.name === columnName);
          }

          let displayName = newShowColumn.displayName;
          if (columnRenames.find((r) => r.originalName === newShowColumn.name)) {
            displayName = columnRenames.find((r) => r.originalName === newShowColumn.name).displayName;
          }

          newState.visualizationQuery.showColumn = newShowColumn;
          newState.visualizationQuery.showColumn.displayName = displayName;
        })
      )
    ),
    setVisualizationQueryExcludedCategorizedByValues: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload: string[]) =>
        produce(oldState, (newState) => {
          (newState.visualizationQuery.showColumn as IPercentOfTotalReportColumn).excludedCategorizedByValues = payload;
        })
      )
    ),
    setSummarySort: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload: IReportConfigurationPresentationSummary) =>
        produce(oldState, (newState) => {
          newState.presentationConfiguration.summary = payload;
        })
      )
    ),
    setLegendSortOrder: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload) =>
        produce(oldState, (newState) => {
          newState.presentationConfiguration.legendSortOrder = payload;
        })
      )
    ),
    setStacking: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload: StackingOptions) =>
        produce(oldState, (newState) => {
          newState.presentationConfiguration.stacking = payload;
        })
      )
    ),
    setLabelsEnabled: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload: boolean) =>
        produce(oldState, (newState) => {
          newState.presentationConfiguration.labelsEnabled = payload;
        })
      )
    ),
    setAxisTitlesEnabled: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload: boolean) =>
        produce(oldState, (newState) => {
          newState.presentationConfiguration.axisTitlesEnabled = payload;
        })
      )
    ),
    setHiddenControls: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload: { column: string; types: ReportControlType[] }) =>
        produce(oldState, (newState) => {
          const payloadMap = payload.types.map((t) =>
            createHiddenControlColumns({ type: t, columnName: payload.column })
          );

          newState.visualizationQuery.hiddenControls = newState.visualizationQuery.hiddenControls
            .filter((c) => c.columnName !== payload.column)
            .concat(payloadMap);
        })
      )
    ),
    setDisplayName: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload: { column: string; displayName: string }) =>
        produce(oldState, (newState) => {
          const existingIndex = oldState.presentationConfiguration.columnNames.findIndex(
            (c) => c.originalName === payload.column
          );

          if (existingIndex !== -1) {
            newState.presentationConfiguration.columnNames.splice(existingIndex, 1);
          }
          newState.presentationConfiguration.columnNames.push(
            createColumnAlias({
              columnIndex: Math.random() * 1000,
              originalName: payload.column,
              displayName: payload.displayName
            })
          );
        })
      )
    ),
    setCategoryColor: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload: { category: string; color: string }) =>
        produce(oldState, (newState) => {
          newState.presentationConfiguration.colors[payload.category] = createSingleColor({
            type: 'custom',
            hexValue: payload.color
          });
        })
      )
    ),
    setReportTableActiveTab: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload: ReportTableTabs) =>
        produce(oldState, (newState) => {
          newState.presentationConfiguration.reportTableActiveTab = payload;
        })
      )
    ),
    setAgGridColumnConfig: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload: IAgGridColumnConfig[]) =>
        produce(oldState, (newState) => {
          newState.presentationConfiguration.agGridColumnConfig = payload;
        })
      )
    ),
    setTarget: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload: IReportTarget) =>
        produce(oldState, (newState) => {
          newState.layers.target = payload;
        })
      )
    ),
    resetTarget: compose.payloadlessAction(
      // draft is the immer draft from produce()
      mutateReportConfigurationStateWithoutPayload((draft) => {
        const columnType = draft.visualizationQuery.categorizedByColumn.columnType;
        const categorizedByColumn = categorizedByNoneColumnObject;
        const target = createReportConfigurationLayers().target;

        if (columnType === EReportColumnTypes.targetStatus) {
          draft.layers.target = target;
          draft.visualizationQuery.categorizedByColumn = categorizedByColumn;
        } else {
          draft.layers.target = target;
        }

        return draft;
      })
    ),
    addDimensionalSummaryQuery: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload) =>
        produce(oldState, (newState) => {
          newState.dimensionalSummaryQueries.push(payload);
          newState.presentationConfiguration.dimensionalSummaryBlockExpandedStates.push(
            createControlBlockIndex({
              controlBlockIndex: newState.dimensionalSummaryQueries.length - 1
            })
          );
        })
      )
    ),
    updateOrAddDimensionalSummaryBlockNames: compose.action(
      mutateCurrentReportConfigurationInState(
        (oldState, payload: { controlBlockIndex: number; originalName: string; displayName: string }) =>
          produce(oldState, (newState) => {
            const indexOfRecordToUpdate = oldState.presentationConfiguration.dimensionalSummaryBlockNames.findIndex(
              (i) => i.controlBlockIndex === payload.controlBlockIndex
            );

            const controlBlockAlias = createControlBlockAlias(payload);

            if (indexOfRecordToUpdate === -1) {
              newState.presentationConfiguration.dimensionalSummaryBlockNames.push(controlBlockAlias);
            } else {
              newState.presentationConfiguration.dimensionalSummaryBlockNames[indexOfRecordToUpdate] =
                controlBlockAlias;
            }
          })
      )
    ),
    setCoaAccountType: compose.action(mutateCurrentReportConfigurationInState(setCoaAccountType)),
    setRowsColumn: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload: IRowsColumnPayload) =>
        produce(oldState, (newState) => {
          const { coaDimensionMetadata, selectedRow, queryIndex } = payload;
          const rowsColumn = deriveRowsColumn(selectedRow, coaDimensionMetadata);

          const { sortColumnName, sortDirection } = deriveNextSort(
            newState.dimensionalSummaryQueries[0].dimension.rowsColumn,
            rowsColumn,
            newState.dimensionalSummaryQueries[0].sortColumnName,
            newState.dimensionalSummaryQueries[0].sortDirection
          );

          newState.dimensionalSummaryQueries[queryIndex].dimension.rowsColumn = rowsColumn;
          newState.dimensionalSummaryQueries[queryIndex].sortColumnName = sortColumnName;
          newState.dimensionalSummaryQueries[queryIndex].sortDirection = sortDirection;
        })
      )
    ),
    setColumnsColumn: compose.action(mutateCurrentReportConfigurationInState(setColumnsColumn)),
    setCoaAccountTypeAndUpdateColumnsColum: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload: IShowCoaAccountWithColumnsColumn) =>
        setColumnsColumn(setCoaAccountType(oldState, payload), payload)
      )
    ),
    toggleShowTotals: compose.payloadlessAction(
      mutateReportConfigurationStateWithoutPayload((draft) => {
        draft.detailTableQuery.showTotals = !draft.detailTableQuery.showTotals;
        return draft;
      })
    ),
    setAccountSummaryShowTotalsRows: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload: boolean) =>
        produce(oldState, (draft) => {
          if (!draft.accountSummary || !isMultiHierarchyAccountSummary(draft.accountSummary)) {
            return;
          }
          draft.accountSummary.showTotalsRows = payload;
        })
      )
    ),
    setAccountSummaryShowRootLevelBreakdowns: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload: boolean) =>
        produce(oldState, (draft) => {
          if (!draft.accountSummary || !isMultiHierarchyAccountSummary(draft.accountSummary)) {
            return;
          }
          draft.accountSummary.showRootLevelBreakdowns = payload;
        })
      )
    ),
    /**
     * Details-tab state (columns, filters, sort, Show Totals) — BUD-18475.
     *
     * Merges rather than replaces, so a column change does not silently discard the user's filters.
     * Lives on `accountSummary` so it travels with saved views the way row groups do; storing it on
     * `detailTableQuery` would put it where a flag-gated backfill rewrites it on read.
     */
    setMhDetailsViewConfig: compose.action(
      mutateCurrentReportConfigurationInState((oldState, payload: Partial<MhDetailsViewConfig>) =>
        produce(oldState, (draft) => {
          if (!draft.accountSummary || !isMultiHierarchyAccountSummary(draft.accountSummary)) {
            return;
          }
          draft.accountSummary.details = { ...draft.accountSummary.details, ...payload };
        })
      )
    ),
    setAccountSummarySelectedColumn: compose.action(setAccountSummarySelectedColumn),
    updateExpandedKeys: compose.action(updateExpandedRowGroups),
    // A collapse the grid makes on its own (a saved expansion whose row has nothing beneath it under the
    // current filters, or the grouping-overflow guard) prunes the stored path like collapseRowGroup but keeps
    // the configuration id: it normalises the configuration that was loaded rather than editing it, and
    // clearing the id flags a freshly loaded saved view as Modified.
    pruneExpandedRowGroup: compose.action((state, payload: currentReportActions.ExpandCollapseRowGroupPayload) =>
      produce(state, (newState) => {
        currentReportActions.collapseRowGroup(newState.currentReportConfiguration, payload);
      })
    ),
    // The same keep-the-id prune for reports still on legacy expandedGroupKeys.
    // EXPANDED_ROWS_MIGRATION: Remove after migration complete.
    pruneExpandedGroupKeys: compose.action((state, payload: IExpandedGroupKeys) =>
      updateExpandedRowGroups(state, payload, true)
    ),

    // Applying any methods from currentReportActions to the action creators we're defining in this module.
    ...mapValues(currentReportActions, (reducer) =>
      compose.action(withImmerReducer(withNewReportConfig<Parameters<typeof reducer>[1]>(reducer)))
    ),

    clearAccountSummaryChart: compose.payloadlessAction(
      mutateReportConfigurationStateWithoutPayload((draft) => {
        if (!draft.accountSummary) return draft;
        draft.accountSummary.charts = [];
        return draft;
      })
    ),

    // And visualizationQueryActions...
    ...mapValues(visualizationQueryActions, (reducer) =>
      compose.action(withImmerReducer(withVisualizationQuery(reducer)))
    ),

    applyNumberFormatting: compose.action(
      mutateCurrentReportConfigurationInState((currentReportConfiguration, formattingOptions: any) =>
        produce(currentReportConfiguration, (newReportConfiguration) => {
          if (!newReportConfiguration.accountSummary) {
            throw new Error('Applying number formatting to a non-account-summary report config');
          }
          newReportConfiguration.accountSummary.formatting = { ...formattingOptions };
        })
      )
    ),
    applyRoundingControl: compose.action(
      mutateCurrentReportConfigurationInState((currentReportConfiguration, roundingControl: IAccountSummaryRounding) =>
        produce(currentReportConfiguration, (newReportConfiguration) => {
          if (!newReportConfiguration.accountSummary) {
            throw new Error('Applying rounding control to a non-account-summary report config');
          }
          newReportConfiguration.accountSummary.rounding = roundingControl;
        })
      )
    ),
    applyHideColumnGroupHeaders: compose.action(mutateCurrentReportConfigurationInState(applyHideColumnGroupHeaders)),
    setHiddenColumns: compose.action(
      mutateCurrentReportConfigurationInState(
        (currentReportConfiguration, hiddenColumns: Record<string, boolean> | undefined) =>
          produce(currentReportConfiguration, (newReportConfiguration) => {
            if (!newReportConfiguration.accountSummary) {
              throw new Error('Trying to set hidden columns in a non-account-summary report config');
            }
            newReportConfiguration.accountSummary.hiddenColumns = { ...hiddenColumns };
          })
      )
    ),
    toggleHiddenLegendItems: compose.action(mutateCurrentReportConfigurationInState(toggleHiddenLegendItems))
  })
);

export const {
  branchKey: reportConfigurationStateBranchKey,
  reducer: reportConfigurationReducer,
  selector: reportConfigurationSelector,
  actionCreators: reportConfigurationActionCreators,
  actions: reportConfigurationActions
} = reportConfigurationState;
